/* Copyright (c) 2017 Backpack Health LLC */

import * as _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import ContentFrame from '../../../applicationFrame/ContentFrame';
import { ScrollAwareContext, ScrollAwareScroller, ScrollAwareAppNavStep } from '../../../components/ScrollAware';
import { AppHeaderScrollableText } from '../../../components/appNav/AppHeaderScrollable';
import DoNotConsentModal, { WithdrawName } from '../../../components/modal/DoNotConsentModal';

import ProfileName from './ProfileName';
import ProfileDateOfBirth from './ProfileDateOfBirth';
import ProfileGender from './ProfileGender.redux';
import ProfileBloodType from './ProfileBloodType.redux';
import ProfileRace from './ProfileRace.redux';
import ProfileResidence from './ProfileResidence';

import UserModel from '../../../../models/UserModel';
import ProfileModel from '../../../../models/ProfileModel';
import GenderModel from '../../../../models/GenderModel';

import ProfileActions from '../../redux/ProfileActions';

import RouteNames from '../../RouteNames';
import ProfilePhrases from '../../../../phraseKeys/Profile.phrase';
import { PolicyName } from '../../../../models/PolicyModel';
import { ProfileThemeedWarningModal } from '../../../components/modal/WarningModal';
import { queryCache } from 'react-query';

class MyProfileEditController extends React.Component {

  constructor(props) {
    super(props);

    this.submitProfileChanges = this.submitProfileChanges.bind(this);
    this.onChangeProfile = this.onChangeProfile.bind(this);
    this.viewIsValid = this.viewIsValid.bind(this);
    this.checkValidation = this.checkValidation.bind(this);

    const profile = new ProfileModel(props.profile.data);
    this.state = {
      profile,
      defaultOtherGenderText: props.intl.formatMessage({ id: 'list.gender.other.name' }),
      profileIsLoaded: !!profile.data.id,
      submitErrorText: '',
      errorMessage: '',
      fieldErrorName: '',
    };
  }

  UNSAFE_componentWillMount() {
    this.props.loadInitialData();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.state.profileIsLoaded) {
      const profile = new ProfileModel(nextProps.profile.data);
      this.setState({
        profile,
        profileIsLoaded: !!profile.data.id,
      });
    }
  }

  onChangeProfile(profileData, callback) {
    this.setState({
      profile: new ProfileModel(profileData),
      fieldErrorName: '',
    }, callback);
  }

  getContent() {
    switch (this.props.propertyName) {
      case 'dob':
        return {
          submitDisabled: false,
          cancel: this.props.goBack,
          isValid: [() => this.state.profile.hasValidDateOfBirth()],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: ProfileDateOfBirth,
          phraseData: ProfilePhrases.profileDateOfBirth(this.props.isMainProfile),
          policyName: PolicyName.DATE_OF_BIRTH,
        };
      case 'gender':
        return {
          submitDisabled: false,
          cancel: this.props.goBack,
          isValid: [() => this.state.profile.hasValidGender(), () => this.state.profile.hasValidBirthSex()],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: ProfileGender,
          phraseData: ProfilePhrases.profileGender(this.props.isMainProfile),
          policyName: PolicyName.GENDER,
        };
      case 'bloodType':
        return {
          submitDisabled: false,
          cancel: this.props.goBack,
          isValid: [() => this.state.profile.hasValidBloodType()],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: ProfileBloodType,
          phraseData: ProfilePhrases.profileBloodType(this.props.isMainProfile),
          policyName: null,
        };
      case 'residence':
        return {
          submitDisabled: false,
          cancel: this.props.goBack,
          isValid: [() => {
            if (!this.state.profile.hasValidResidence()) {
              return false;
            } else if (this.props.isResidenceStateRequired(this.state.profile.residenceCountryId)) {
              return !!this.state.profile.residenceStateId;
            }

            return true;
          }],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: ProfileResidence,
          phraseData: ProfilePhrases.profileResidence(this.props.isMainProfile),
          policyName: PolicyName.RESIDENCE,
          submit: () => {
            if (!this.checkValidation()) { return; }

            const newResidenceSupportsIntegrations = this.props.isIntegrationsSupportedByCountry(this.state.profile.residenceCountryId);
            const willLoseIntegrations = this.props.hasIntegrations && !newResidenceSupportsIntegrations;

            if (willLoseIntegrations) {
              this.props.showSystemModal(
                <ProfileThemeedWarningModal
                  phraseMessages={{
                    title: { id: 'dataConnectionsRemove.countryUnavailable.heading' },
                    subheading: { id: 'dataConnectionsRemove.countryUnavailable.subheading' },
                    body: { id: 'dataConnectionsRemove.countryUnavailable.message.pre' },
                    button: { id: 'dataConnectionsRemove.countryUnavailable.confirm.button.label' },
                  }}
                  onConfirm={this.submitProfileChanges}
                  hasCloseButton={true}
                  isBodyPreformatted={true}
                />
              );
            } else {
              this.submitProfileChanges();
            }
          },
        };
      case 'race':
        return {
          submitDisabled: false,
          cancel: this.props.goBack,
          isValid: [() => this.state.profile.hasValidRace()],
          viewErrorMessage: [''],
          fieldErrorName: [''],
          content: ProfileRace,
          phraseData: ProfilePhrases.profileRace(this.props.isMainProfile),
          next: this.props.goToRaceSelfIdentity,
          submit: null,
          policyName: PolicyName.RACE,
        };
      case 'name':
      default:
        return {
          submitDisabled: false,
          cancel: this.props.goBack,
          isValid: [() => this.state.profile.hasGivenOrFamilyName(), () => this.state.profile.hasPreferredName()],
          viewErrorMessage: ['error.name-validator.missing-given-family-names.message'],
          fieldErrorName: ['', 'preferred_name'],
          content: ProfileName,
          phraseData: ProfilePhrases.profileName(this.props.isMainProfile),
          onDisabledClick: this.checkValidation,
          policyName: PolicyName.NAME,
        };
    }
  }

  checkValidation() {
    const viewData = this.getContent();
    const validators = viewData.isValid;

    for (let i = 0; i < validators.length; i++) {
      if (!validators[i]()) {
        this.props.showNotification(viewData.viewErrorMessage[i]);
        this.setState({ fieldErrorName: viewData.fieldErrorName[i] });
        return false;
      }
    }

    return true;
  }

  submitProfileChanges(e) {
    if (e) { e.preventDefault(); }
    if (!this.checkValidation()) { return; }

    const viewData = this.getContent();

    this.state.profile.cleanNames();
    const profileWithValidValues = _.pickBy(this.state.profile.data, (val) => val !== null);
    const genderData = this.props.genderIdMap[profileWithValidValues.gender_id] || {};
    if (!GenderModel.isOther(genderData)) {
      profileWithValidValues.other_gender = '';
    }

    ProfileActions.api.updateProfile(profileWithValidValues, (apiError) => {
      if (apiError) {
        if (apiError.isUnavailableForLegalReasons()) {
          this.props.showSystemModal(<DoNotConsentModal withdrawName={WithdrawName.tooYoungAfterSetup} policyName="" isShared={false} />);
        } else {
          this.props.showNotification(apiError.messageKey());
        }
      } else {
        this.props.requestActiveProfile();
        queryCache.invalidateQueries('profile');

        if (viewData.completeTo) {
          viewData.completeTo();
        } else {
          this.props.goBack();
        }
      }
    });
  }

  viewIsValid() {
    const viewData = this.getContent();
    const validators = viewData.isValid;

    for (let i = 0; i < validators.length; i++) {
      if (!validators[i]()) {
        return false;
      }
    }

    return true;
  }

  render() {
    if (!this.props.propertyName) {
      return <Redirect to={RouteNames.profile()} />;
    } else {
      const viewData = this.getContent();
      return (
        <div>
          <ScrollAwareContext>
            <ScrollAwareAppNavStep
              submitDisabled={!this.viewIsValid()}
              cancel={viewData.cancel}
              goBack={viewData.goBack}
              submit={'submit' in viewData ? viewData.submit : this.submitProfileChanges}
              next={viewData.next}
              onDisabledClick={viewData.onDisabledClick}
              color="white"
            />

            <ContentFrame>
              <ScrollAwareScroller>
                <AppHeaderScrollableText
                  id={viewData.phraseData.title.id}
                  values={{ string1: this.state.profile.greetingName() }}
                />
                {
                  this.state.profileIsLoaded ? (
                    <viewData.content
                      profileData={this.state.profile.data}
                      submitForm={this.submitProfileChanges}
                      onChange={this.onChangeProfile}
                      submitErrorText={this.state.submitErrorText}
                      fieldErrorName={this.state.fieldErrorName}
                      clearViewErrors={this.props.dismissNotification}
                      clearFieldErrors={() => { this.setState({ fieldErrorName: '' }); }}
                      goToRaceSelfIdentity={this.props.goToRaceSelfIdentity}
                      defaultOtherGenderText={this.state.defaultOtherGenderText}
                      initialBirthSexId={this.props.profile.getBirthSexId()}
                      phraseData={viewData.phraseData}
                      isInitialSetup={false}
                      allowWithdraw={this.props.isMainProfile || !this.props.profile.hasUser}
                      isMainProfile={this.props.isMainProfile}
                      onWithdrawConsent={() => {
                        let name = '';
                        if (this.props.isMainProfile) {
                          name = WithdrawName.afterSetup;
                        } else if (this.props.hasSoleAccess) {
                          name = WithdrawName.afterDependentSetupSoleAccess;
                        } else {
                          name = WithdrawName.afterDependentSetupSharedAccess;
                        }

                        this.props.showSystemModal(<DoNotConsentModal withdrawName={name} policyName={viewData.policyName} isShared={!this.props.hasSoleAccess} />);
                      }}
                    />
                  ) : null
                }
              </ScrollAwareScroller>
            </ContentFrame>
          </ScrollAwareContext>
        </div>
      );
    }
  }
}
MyProfileEditController.propTypes = {
  intl: PropTypes.object.isRequired,
  isMainProfile: PropTypes.bool,
  propertyName: PropTypes.string,
  goBack: PropTypes.func.isRequired,
  user: PropTypes.instanceOf(UserModel).isRequired,
  profile: PropTypes.instanceOf(ProfileModel).isRequired,
  requestActiveProfile: PropTypes.func.isRequired,
  goToRaceSelfIdentity: PropTypes.func.isRequired,
  goBackToGender: PropTypes.func.isRequired,
  goBackToRace: PropTypes.func.isRequired,
  goBackToProfile: PropTypes.func.isRequired,
  loadInitialData: PropTypes.func.isRequired,
  genderIdMap: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
  dismissNotification: PropTypes.func,
  showSystemModal: PropTypes.func,
  isResidenceStateRequired: PropTypes.func.isRequired,
  hasSoleAccess: PropTypes.bool,
  hasIntegrations: PropTypes.bool,
  isIntegrationsSupportedByCountry: PropTypes.func,
};

export default MyProfileEditController;
